import { createContext } from "react";

import { env } from "@/config/env";

export interface FeatureFlagsContextValue {
  /**
   * Complete Slice 6 experience, including Markdoc docs and API Reference
   * (`api-portal-sandbox-credentials`). Fail-closed to the current `main`
   * experience when LD is on and the flag is off or unavailable.
   */
  sandboxCredentialsEnabled: boolean;
  /**
   * Shared Developer Platform landing at `/portal`
   * (`api-portal-multi-platform-docs`). Fail-closed: `#/` stays on Glofox docs
   * when LD is on and the flag is off or unavailable.
   */
  multiPlatformDocsEnabled: boolean;
  /**
   * False while LaunchDarkly is still evaluating for the current identity.
   * The fail-closed values are indistinguishable from a real "off", so acting
   * on them early would send a remembered session to the legacy experience.
   */
  flagsResolved: boolean;
}

/** Flag values shared with the Markdoc docs site; excludes evaluation state. */
export type MirroredFeatureFlags = Pick<
  FeatureFlagsContextValue,
  "sandboxCredentialsEnabled" | "multiPlatformDocsEnabled"
>;

/**
 * Defaults when LaunchDarkly is not configured (local).
 * Slice 6 stays on so local development matches `development` without an SDK key.
 * The landing stays off so Cypress `@home` still reaches Glofox docs; set
 * `VITE_MULTI_PLATFORM_DOCS_ENABLED=true` to preview it locally.
 * With LD enabled, evaluation overrides these values (fail-closed to false).
 */
export const defaultFeatureFlags: FeatureFlagsContextValue = {
  sandboxCredentialsEnabled: true,
  multiPlatformDocsEnabled: env.multiPlatformDocsEnabled,
  flagsResolved: true,
};

export const FeatureFlagsContext =
  createContext<FeatureFlagsContextValue>(defaultFeatureFlags);
